import React, { useCallback } from 'react';
import {
  Box,
  IconButton,
  MobileStepper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Settings as SettingsIcon,
  DeleteOutline as RemoveIcon,
} from '@mui/icons-material';
import type { DashboardWidgetInstance, DashboardTheme, WidgetStackNavStyle } from './types';
import { cardStyleFromWidget, contentStyleFromWidget } from './dashboardStyleUtils';

export interface WidgetStackContainerProps {
  childIds: string[];
  widgets: Record<string, DashboardWidgetInstance>;
  activeIndex: number;
  navStyle: WidgetStackNavStyle;
  editMode: boolean;
  theme?: DashboardTheme;
  tabLabels?: Record<string, string>;
  onActiveChange: (index: number) => void;
  onOpenChildSettings: (childId: string) => void;
  onRemoveChild: (childId: string) => void;
  renderWidgetBody: (instance: DashboardWidgetInstance, layoutItemId: string) => React.ReactNode;
}

const WIDGET_TYPE_LABELS: Partial<Record<string, string>> = {
  placeholder: 'Placeholder',
  iframe: 'Embed',
  text: 'Text',
  viz_tabs: 'Charts',
  chart_bar: 'Bar',
  chart_line: 'Line',
  chart_area: 'Area',
  chart_donut: 'Donut',
  chart_radar: 'Radar',
  chart_scatter: 'Scatter',
  chart_treemap: 'Treemap',
  calendar_heatmap: 'Heatmap',
  gauge: 'Gauge',
  indicator: 'Indicator',
  data_table: 'Table',
  web_map: 'Map',
  spatial_analytics: 'Analytics',
};

function childLabel(w: DashboardWidgetInstance | undefined, idx: number): string {
  if (!w) return `Layer ${idx + 1}`;
  if (w.config?.customTitle?.trim()) return w.config.customTitle.trim();
  if (w.type === 'text' && w.config?.title?.trim()) return w.config.title.trim();
  return WIDGET_TYPE_LABELS[w.type] ?? `Layer ${idx + 1}`;
}

const WidgetStackContainer: React.FC<WidgetStackContainerProps> = ({
  childIds,
  widgets,
  activeIndex,
  navStyle,
  editMode,
  theme,
  tabLabels,
  onActiveChange,
  onOpenChildSettings,
  onRemoveChild,
  renderWidgetBody,
}) => {
  const safeIndex = Math.max(0, Math.min(activeIndex, childIds.length - 1));
  const activeChildId = childIds[safeIndex];
  const activeChild = activeChildId ? widgets[activeChildId] : undefined;

  const handleNext = useCallback(() => {
    onActiveChange(Math.min(safeIndex + 1, childIds.length - 1));
  }, [safeIndex, childIds.length, onActiveChange]);

  const handleBack = useCallback(() => {
    onActiveChange(Math.max(safeIndex - 1, 0));
  }, [safeIndex, onActiveChange]);

  if (childIds.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Empty stack — drag a widget onto this tile to add layers.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {navStyle === 'tabs' && (
        <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={safeIndex}
            onChange={(_, v) => onActiveChange(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 1.5, fontSize: '0.75rem' } }}
          >
            {childIds.map((cid, idx) => (
              <Tab key={cid} label={tabLabels?.[cid]?.trim() || childLabel(widgets[cid], idx)} />
            ))}
          </Tabs>
          {editMode && activeChildId && (
            <>
              <Tooltip title="Child widget settings">
                <IconButton
                  size="small"
                  onClick={() => onOpenChildSettings(activeChildId)}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <SettingsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove from stack">
                <IconButton
                  size="small"
                  onClick={() => onRemoveChild(activeChildId)}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )}

      <Box sx={{
        flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        ...(activeChild ? cardStyleFromWidget(activeChild.config?.style, theme) : {}),
        ...(activeChild ? contentStyleFromWidget(activeChild.config?.style, theme) : {}),
      }}>
        {activeChild ? renderWidgetBody(activeChild, activeChildId) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Child widget not found.
          </Typography>
        )}
      </Box>

      {navStyle === 'carousel' && (
        <MobileStepper
          variant="dots"
          steps={childIds.length}
          position="static"
          activeStep={safeIndex}
          sx={{
            flexShrink: 0,
            bgcolor: 'transparent',
            px: 1,
            py: 0.25,
            minHeight: 32,
            '& .MuiMobileStepper-dot': { mx: 0.25, width: 6, height: 6 },
          }}
          nextButton={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {editMode && activeChildId && (
                <>
                  <Tooltip title="Child settings">
                    <IconButton size="small" onClick={() => onOpenChildSettings(activeChildId)} onMouseDown={(e) => e.stopPropagation()}>
                      <SettingsIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from stack">
                    <IconButton size="small" onClick={() => onRemoveChild(activeChildId)} onMouseDown={(e) => e.stopPropagation()}>
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <IconButton size="small" onClick={handleNext} disabled={safeIndex >= childIds.length - 1}>
                <KeyboardArrowRight />
              </IconButton>
            </Box>
          }
          backButton={
            <IconButton size="small" onClick={handleBack} disabled={safeIndex <= 0}>
              <KeyboardArrowLeft />
            </IconButton>
          }
        />
      )}
    </Box>
  );
};

export default WidgetStackContainer;
